import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import toast from 'react-hot-toast';
import { TableSkeleton } from '../../Components/common/Skeleton';
import DoctorRegisterModal from '../../Components/doctors/DoctorRegisterModal';
import { UserPlus } from 'lucide-react';
import Pagination from '../../Components/common/Pagination';
import { getAvatarUrl } from '../../utils/avatar';

const DoctorList = () => {
  const { user } = useAuth();
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isFetching, setIsFetching] = useState(false);
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const [filters, setFilters] = useState({
    specialization: '',
    verificationStatus: '',
    search: ''
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    pages: 0
  });

  useEffect(() => {
    fetchDoctors();
  }, [pagination.page, filters]);

  const fetchDoctors = async () => {
    try {
      if (doctors.length > 0) {
        setIsFetching(true);
      }
      const params = new URLSearchParams({
        page: pagination.page,
        limit: pagination.limit,
        ...(filters.specialization && { specialization: filters.specialization }),
        ...(filters.verificationStatus && { verificationStatus: filters.verificationStatus }),
        ...(filters.search && { search: filters.search })
      });

      const response = await api.get(`/doctors?${params}`);
      setDoctors(response.data.data.doctors || []);
      setPagination((prev) => ({
        ...prev,
        total: response.data.data.pagination.total || 0,
        pages: response.data.data.pagination.pages || 0
      }));
    } catch (error) {
      toast.error('Failed to load doctors');
    } finally {
      setLoading(false);
      setIsFetching(false);
    }
  };


  const handleDeleteDoctor = async (doctorId, doctorName) => {
    if (window.confirm(`Are you sure you want to permanently delete ${doctorName} and their associated user account? This action cannot be undone.`)) {
      try {
        await api.delete(`/doctors/${doctorId}`);
        toast.success('Doctor deleted successfully');
        fetchDoctors();
      } catch (error) {
        toast.error(error.response?.data?.message || 'Failed to delete doctor');
      }
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handlePageChange = (newPage) => {
    setPagination((prev) => ({ ...prev, page: newPage }));
  };

  const getVerificationColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800',
      verified: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="h-8 w-32 bg-slate-200 animate-pulse rounded-lg"></div>
        </div>
        <div className="bg-white rounded-3xl shadow-xl shadow-slate-200/50 border border-slate-100 p-8">
          <TableSkeleton rows={8} />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" style={{ minHeight: '80vh' }}>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <h1 className="text-3xl font-black text-[#143E78] tracking-tight">Doctor Registry</h1>
        {(user?.role === 'admin' || user?.role === 'customer_care' || user?.role === 'cc_head') && (
          <button
            onClick={() => setShowRegisterModal(true)}
            className="flex items-center gap-2 bg-[#143E78] hover:bg-blue-800 text-white px-6 py-3 rounded-2xl font-black transition-all shadow-lg shadow-blue-900/10 active:scale-95 uppercase text-xs tracking-widest"
          >
            <UserPlus size={18} />
            Register Doctor
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-2xl sm:rounded-3xl shadow-xl shadow-slate-200/50 border border-slate-100 p-4 sm:p-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          <div>
            <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 ml-1">Search</label>
            <input
              type="text"
              name="search"
              value={filters.search}
              onChange={handleFilterChange}
              placeholder="Search by name"
              className="w-full px-4 py-3 bg-slate-50 border-2 border-transparent focus:border-indigo-600 focus:bg-white rounded-xl outline-none font-bold transition-all text-sm"
            />
          </div>
          <div>
            <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 ml-1">Specialization</label>
            <input
              type="text"
              name="specialization"
              value={filters.specialization}
              onChange={handleFilterChange}
              placeholder="Filter by specialization"
              className="w-full px-4 py-3 bg-slate-50 border-2 border-transparent focus:border-indigo-600 focus:bg-white rounded-xl outline-none font-bold transition-all text-sm"
            />
          </div>
          <div className="sm:col-span-2 lg:col-span-1">
            <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 ml-1">Verification Status</label>
            <select
              name="verificationStatus"
              value={filters.verificationStatus}
              onChange={handleFilterChange}
              className="w-full px-4 py-3 bg-slate-50 border-2 border-transparent focus:border-indigo-600 focus:bg-white rounded-xl outline-none font-bold transition-all text-sm"
            >
              <option value="">All Status</option>
              <option value="pending">Pending</option>
              <option value="verified">Verified</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>
        </div>
      </div>

      {/* Doctors Content */}
      <div
        className={`bg-white rounded-lg shadow overflow-hidden transition-opacity duration-300 ${isFetching ? 'opacity-40 pointer-events-none' : 'opacity-100'}`}
      >
        {/* Mobile View: Handy Cards */}
        <div key={pagination.page} className="block lg:hidden divide-y divide-gray-100 animate-fadeIn">
          {doctors.length > 0 ? (
            doctors.map((doctor) => {
              const firstName = doctor.user?.firstName || '';
              const lastName = doctor.user?.lastName || '';
              const displayName = firstName || lastName
                ? (firstName.toLowerCase().startsWith('dr.') ? `${firstName} ${lastName}` : `Dr. ${firstName} ${lastName}`)
                : 'Unknown Doctor';

              return (
                <div key={doctor._id} className="p-4 space-y-3">
                  <div className="flex justify-between items-start">
                    <div className="flex items-center gap-3">
                      {(() => {
                        const avatarUrl = doctor.user?.avatar || doctor.documents?.find(d => d.type === 'PHOTO')?.url;
                        return (
                          <div className="w-10 h-10 rounded-full bg-indigo-50 border border-indigo-100 flex items-center justify-center overflow-hidden shrink-0 shadow-xs">
                            {avatarUrl ? (
                              <img
                                src={getAvatarUrl(avatarUrl)}
                                alt={displayName}
                                className="w-full h-full object-cover"
                                onError={(e) => { e.target.style.display = 'none'; }}
                              />
                            ) : (
                              <span className="text-xs font-black text-indigo-600">
                                {(firstName?.[0] || 'D').toUpperCase()}{(lastName?.[0] || 'R').toUpperCase()}
                              </span>
                            )}
                          </div>
                        );
                      })()}
                      <div className="space-y-0.5">
                        <div className="text-sm font-bold text-gray-900">{displayName}</div>
                        <div className="text-xs text-gray-500">{doctor.specialization || 'N/A'}</div>
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      <span className={`px-2 py-0.5 text-[10px] font-bold rounded-full ${getVerificationColor(doctor.verificationStatus)}`}>
                        {doctor.verificationStatus}
                      </span>
                      <span className={`px-2 py-0.5 text-[9px] font-bold rounded-full ${
                        doctor.showOnWebsite !== false 
                          ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' 
                          : 'bg-amber-50 text-amber-700 border border-amber-200'
                      }`}>
                        {doctor.showOnWebsite !== false ? '● Web: Visible' : '○ Web: Hidden'}
                      </span>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4 pt-2 border-t border-gray-50">
                    <div>
                      <p className="text-[10px] text-gray-500 uppercase font-medium tracking-wider">Experience</p>
                      <p className="text-sm text-gray-900">{doctor.totalExperience || 0} years</p>
                    </div>
                    <div>
                      <p className="text-[10px] text-gray-500 uppercase font-medium tracking-wider">Fee</p>
                      <p className="text-sm text-gray-900">₹{(doctor.financials?.consultationFee || 0).toLocaleString()}</p>
                    </div>
                    <div>
                      <p className="text-[10px] text-gray-500 uppercase font-medium tracking-wider">Type</p>
                      <p className="text-sm text-gray-900">{doctor.doctorType || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-[10px] text-gray-500 uppercase font-medium tracking-wider">Hospital</p>
                      <p className="text-xs text-gray-900 truncate">{doctor.workingHospital || 'N/A'}</p>
                    </div>
                  </div>

                  <Link
                    to={`/doctors/${doctor._id}`}
                    className="block w-full text-center py-2 bg-gray-50 text-blue-600 text-xs font-bold rounded-lg border border-gray-100"
                  >
                    View Profile
                  </Link>
                </div>
              );
            })
          ) : (
            <div className="p-10 text-center text-gray-400 italic text-sm">
              No doctors found
            </div>
          )}
        </div>

        {/* Desktop View: Original Table */}
        <div className="hidden lg:block overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="sticky left-0 bg-gray-50 z-20 px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[200px] border-r border-gray-200 shadow-sm">
                  Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[150px]">
                  Specialization
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[120px]">
                  Experience
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[150px]">
                  Consultation Fee
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[180px]">
                  Contact
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[180px]">
                  CC (VERIFIED BY)
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[120px]">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[150px]">
                  Hospital
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[120px]">
                  Verification
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[100px]">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody key={pagination.page} className="bg-white divide-y divide-gray-200 animate-fadeIn">
              {doctors.length > 0 ? (
                doctors.map((doctor) => {
                  const firstName = doctor.user?.firstName || '';
                  const lastName = doctor.user?.lastName || '';
                  const displayName = firstName || lastName
                    ? (firstName.toLowerCase().startsWith('dr.') ? `${firstName} ${lastName}` : `Dr. ${firstName} ${lastName}`)
                    : 'Unknown Doctor';

                  return (
                    <tr key={doctor._id} className="group hover:bg-gray-50">
                      <td className="sticky left-0 bg-white group-hover:bg-gray-50 z-10 transition-colors px-6 py-4 whitespace-nowrap border-r border-gray-200 shadow-sm">
                        <div className="flex items-center gap-3">
                          {(() => {
                            const avatarUrl = doctor.user?.avatar || doctor.documents?.find(d => d.type === 'PHOTO')?.url;
                            return (
                              <div className="w-9 h-9 rounded-full bg-indigo-50 border border-indigo-100 flex items-center justify-center overflow-hidden shrink-0 shadow-xs">
                                {avatarUrl ? (
                                  <img
                                    src={getAvatarUrl(avatarUrl)}
                                    alt={displayName}
                                    className="w-full h-full object-cover"
                                    onError={(e) => { e.target.style.display = 'none'; }}
                                  />
                                ) : (
                                  <span className="text-[11px] font-black text-indigo-600">
                                    {(firstName?.[0] || 'D').toUpperCase()}{(lastName?.[0] || 'R').toUpperCase()}
                                  </span>
                                )}
                              </div>
                            );
                          })()}
                          <div className="text-sm font-medium text-gray-900">
                            {displayName}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{doctor.specialization || 'N/A'}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{doctor.totalExperience || 0} years</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">₹{(doctor.financials?.consultationFee || 0).toLocaleString()}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-[11px] font-bold text-slate-900">{doctor.user?.phone || 'N/A'}</div>
                        <div className="text-[10px] text-slate-500 lowercase">{doctor.user?.email || 'N/A'}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex flex-col gap-1.5">
                          <div className="flex items-center gap-2">
                            <span className="w-8 text-center text-[9px] font-black bg-slate-100 text-slate-500 px-1 py-0.5 rounded border border-slate-200">INV</span>
                            <span className="text-[10px] font-bold text-slate-700 truncate max-w-[120px]">
                              {doctor.user?.invitedBy ? `${doctor.user.invitedBy.firstName} ${doctor.user.invitedBy.lastName}` : 'System'}
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="w-8 text-center text-[9px] font-black bg-indigo-100 text-indigo-500 px-1 py-0.5 rounded border border-indigo-200">VER</span>
                            <span className="text-[10px] font-bold text-indigo-700 truncate max-w-[120px]">
                              {doctor.verifiedBy ? `${doctor.verifiedBy.firstName} ${doctor.verifiedBy.lastName}` : 'Pending'}
                            </span>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-0.5 text-[10px] font-black rounded-lg ${doctor.doctorType === 'Surgeon' ? 'bg-orange-50 text-orange-600 border border-orange-100' : 'bg-blue-50 text-blue-600 border border-blue-100'}`}>
                          {doctor.doctorType?.toUpperCase() || 'N/A'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-[11px] font-medium text-gray-600 truncate max-w-[150px]">
                          {doctor.workingHospital || 'N/A'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex flex-col gap-1.5">
                          <span className={`px-2 py-0.5 text-xs font-bold rounded-full text-center ${getVerificationColor(doctor.verificationStatus)}`}>
                            {doctor.verificationStatus}
                          </span>
                          <span className={`px-2 py-0.5 text-[10px] font-bold rounded-full text-center ${
                            doctor.showOnWebsite !== false 
                              ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' 
                              : 'bg-amber-50 text-amber-700 border border-amber-200'
                          }`}>
                            {doctor.showOnWebsite !== false ? '● Web: Visible' : '○ Web: Hidden'}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <div className="flex gap-2">
                          <Link to={`/doctors/${doctor._id}`} className="inline-flex items-center px-3 py-1 bg-primary-50 text-primary-700 rounded-lg font-bold hover:bg-primary-100 transition-colors">
                            View
                          </Link>
                          {(user?.role === 'admin') && (
                            <button
                              onClick={() => handleDeleteDoctor(doctor._id, displayName)}
                              className="inline-flex items-center px-3 py-1 bg-red-50 text-red-600 rounded-lg font-bold hover:bg-red-100 transition-colors"
                            >
                              Delete
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="8" className="px-6 py-8 text-center text-gray-500 italic">
                    No doctors found matching the criteria
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>


      {/* Pagination */}
      {pagination.pages > 1 && (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 py-4">
          <div className="text-[10px] font-black text-slate-400 uppercase tracking-widest order-2 sm:order-1">
            Displaying {doctors.length > 0 ? (pagination.page - 1) * pagination.limit + 1 : 0}-{(pagination.page - 1) * pagination.limit + doctors.length} of {pagination.total} records
          </div>
          <div className="order-1 sm:order-2 w-full sm:w-auto overflow-x-auto pb-2 sm:pb-0">
            <Pagination
              currentPage={pagination.page}
              totalPages={pagination.pages}
              onPageChange={handlePageChange}
            />
          </div>
        </div>
      )}
      {/* Registration Modal */}
      <DoctorRegisterModal
        isOpen={showRegisterModal}
        onClose={() => setShowRegisterModal(false)}
        onSuccess={fetchDoctors}
      />
    </div>
  );
};

export default DoctorList;
